/* ═══════════════════════════════════════════════════════════════
   ManualIssuanceService — manual-level issuance records via EC2 API.

   One project = one Manual. Issuing the manual freezes per-section
   snapshots (ProjectSpecService.createIssuance — unchanged) and appends
   ONE row here carrying the manifest: which sections were included,
   which per-section snapshot each points at, and which changed.

   Wired exactly like every other project-spec table: cloudCache
   (optimistic in-memory cache) → apiClient (generic data API) →
   ec2-api/server.js (ALLOWED_TABLES + TABLE_PERMS) → PostgreSQL
   (migration 036, table project_manual_issuances).
   ═══════════════════════════════════════════════════════════════ */

import type { ManualIssuanceManifestEntry, ProjectManualIssuance } from '../../shared/types';
import * as cache from './cloudCache';
import type { TableOpts } from './cloudCache';

const TABLE = 'project_manual_issuances';

const opts: TableOpts = { jsonFields: ['manifest'] };

/** Same id shape the rest of the app mints, so rows stay recognisable. */
export function newManualIssuanceId(): string {
  return 'mi-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

export const ManualIssuanceService = {
  /** Every manual issuance the firm has recorded. */
  list: async (): Promise<ProjectManualIssuance[]> =>
    cache.list<ProjectManualIssuance>(TABLE, opts),

  /** This project's manual issuances, oldest first (creation order). */
  listByProject: async (projectId: string): Promise<ProjectManualIssuance[]> => {
    const all = await ManualIssuanceService.list();
    return all
      .filter((mi) => mi.projectId === projectId)
      .sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
  },

  /** Record a manual issuance. The per-section snapshots referenced by the
   *  manifest must already exist (created via ProjectSpecService.createIssuance
   *  in the same issue flow). */
  create: async (rec: {
    projectId: string;
    name: string;
    date?: string;
    notes?: string;
    issuedBy?: string;
    /** The deliverable this issuance stamps issued (server-side, same write). */
    deliverableId?: string;
    manifest: ManualIssuanceManifestEntry[];
  }): Promise<ProjectManualIssuance> => {
    const row: ProjectManualIssuance = {
      id: newManualIssuanceId(),
      projectId: rec.projectId,
      name: rec.name,
      date: rec.date || new Date().toISOString(),
      notes: rec.notes,
      issuedBy: rec.issuedBy,
      ...(rec.deliverableId ? { deliverableId: rec.deliverableId } : {}),
      manifest: rec.manifest,
      createdAt: new Date().toISOString(),
    };
    return cache.save(TABLE, row, opts);
  },
};
